"use client";

import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import LanguageIcon from "@mui/icons-material/Language";
import cn from "classnames";
import { useParams } from "next/navigation";
import { useCallback, useRef, useState } from "react";
import ActiveAnchor from "@/components/common/ActiveAnchor";
import Button, { type RegularButton } from "@/components/common/Button";
import List from "@/components/common/List";
import { ButtonSize } from "@/constants/button";
import { useClickOutside } from "@/hooks/useClickOutside";
import { useTranslation } from "@/i18n/client";
import { getLocaleFromURLParams } from "@/utils/locale/getLocaleFromURLParams";
import { type LocalizedPath } from "../index";
import s from "./DropdownSelector.module.css";

export interface DropdownSelectorProps {
  list: LocalizedPath[];
  buttonProps?: Omit<RegularButton, "onClick">;
}

export default function DropdownSelector(props: DropdownSelectorProps) {
  const { list, buttonProps } = props;
  const wrapperRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();
  const params = useParams();
  const [open, setOpen] = useState(false);
  const activeLanguage = getLocaleFromURLParams(params);

  const handleOpen = useCallback(() => {
    setOpen((prevOpen) => !prevOpen);
  }, []);
  const handleClose = useCallback(() => {
    setOpen(false);
  }, []);

  useClickOutside(wrapperRef, handleClose, open);

  return (
    <div className={s.wrapper} ref={wrapperRef}>
      <Button
        onClick={handleOpen}
        StartIcon={LanguageIcon}
        EndIcon={ArrowDropDownIcon}
        size={ButtonSize.Small}
        {...buttonProps}
        className={cn(s.button, buttonProps?.className)}
      >
        {activeLanguage.toUpperCase()}
      </Button>

      {open && (
        <List
          items={list}
          renderItem={(localeItem) => (
            <ActiveAnchor
              href={localeItem.path}
              extendedURI={false}
              onClick={handleClose}
            >
              {t(`languages.${localeItem.locale.toUpperCase()}.label`)}
            </ActiveAnchor>
          )}
          className={s.list}
        />
      )}
    </div>
  );
}
